// Global Instructions Rule Applied!

import React, { useCallback, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useCurrencies } from '../../../currencies/useCurrencies.ts'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { updateProspectRecord } from '../../../../state_manager/state_update_actions/fundraising/index.ts'
import { updateCompanyRecord } from '../../../../state_manager/state_update_actions/companies/index.ts'
import { updateContactRecord } from '../../../../state_manager/state_update_actions/contacts/index.ts'
import { FieldTypeEnum } from '../../../../core/components/InputManager/types.ts'
import { type ProspectRecord, ProspectRecordFields } from '../../../../state_manager/data_models/prospect_record.ts'
import { useStageMoveRights } from '../../../../core/hooks/useStageMoveRights.ts'
import { PIPELINE_TYPE_FUNDRAISING } from '../../../../state_manager/globals.ts'
import { ProspectFields } from '../../utils/fieldConfigs.ts'
import { AccountRecordFields } from '../../../../state_manager/data_models/account_record.ts'
import { fetchFundraisingById } from '../../services/fundraisingService.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { store, type RootState } from '../../../../state_manager/store.ts'
import { createTableColumns } from '../../../../ui_configs/table_grid_columns.ts'
import {
  applyFundChangeToUpdates,
  getEffectiveFundraisingPipelineIdForProspect,
  normalizeFundIdFromPatch,
  syncFundChangePipelineOnServer
} from '../../utils/pipelineFundStageRules.ts'
import { VIEW_TASK_CONFIG } from '../../../../ui_configs/fund_management/view_task_config.ts'
import type { ViewConfigColumn } from '../../../../core/components/ViewConfig/types.ts'
import {
  mapColumnToInputManagerField,
  reportingModuleKeyToTableInputManagerModule,
  type TableInputManagerModule
} from '../../../shared/table_input_manager_mappers/index.ts'

import TableView from '../../../../core/components/TableView/TableView.tsx'
import { useFundFieldOptions } from '../../../global_configs/hooks/useFundFieldOptions.tsx'
import type { FilterConfig } from '../../../../core/components/TableView/TableViewTypes.ts'

function findProspectRowInTable(data: ProspectRecord[], recordId: string): ProspectRecord | undefined {
  return data.find(
    (p) =>
      p.id?.toString() === recordId ||
      p[ProspectFields.prospects__id]?.toString() === recordId ||
      (p as { prospects__id?: string }).prospects__id?.toString() === recordId
  )
}

interface FundraisingTableProps {
  /** Column definitions from global configs (`reporting_column_defaults` + per-report `reporting_columns`) + user prefs. */
  gridColumns: ViewConfigColumn[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  currentPage: number
  activeFilters: any[]
  activeSorting: any
  filterOptions: any
  filterOptionsLoading: boolean
  onLoadMore: (page: number, pageSize: number) => void
  onFiltersChange: (filters: FilterConfig) => void
  onSortChange: (sortConfig: any) => void
  onProspectNameClick: (key: string, value: any, recordId: string | number) => void
  onTasksClick?: (allTasks: any[], record: any, taskId: string | null) => void
  onTaskToggle?: (taskId: string, recordId: string, completed: boolean) => void
  getLookupOptions: (category: string) => any[]
  getLookupLabel: (lookupId: string) => string
}

/**
 * Fundraising table component with virtualized scrolling and editing capabilities
 */
const FundraisingTable: React.FC<FundraisingTableProps> = ({
  gridColumns,
  loading,
  loadingMore,
  hasMore,
  currentPage,
  activeFilters,
  activeSorting,
  filterOptions,
  filterOptionsLoading,
  onLoadMore,
  onFiltersChange,
  onSortChange,
  onProspectNameClick,
  onTasksClick,
  onTaskToggle,
  getLookupOptions,
  getLookupLabel
}) => {
  const dispatch = useDispatch()

  const accountRoleChoices = useSelector((state: any) => state.globalConfigs?.account_role_choices || [])
  const fundsData = useSelector((state: any) => state.globalConfigs?.funds || [])

  const getRoleOptions = useCallback(() => {
    return accountRoleChoices.map((choice: { id: string; role?: string }) => ({
      value: choice.id,
      label: choice.role || ''
    }))
  }, [accountRoleChoices])

  const getFundOptions = useCallback(() => {
    return fundsData.map((fund: { id: string; name?: string }) => ({
      value: fund.id,
      label: fund.name || ''
    }))
  }, [fundsData])

  // Get updated fundraising data from Redux state
  const fundraisingData = useSelector((state: RootState) => state.fundraising?.data || [])

  // Get pipeline configs and stages from Redux state
  const pipelines = useSelector((state: any) => state.globalConfigs?.pipelines || [])
  const pipelineConfigs = useSelector((state: RootState) => state.globalConfigs?.pipeline_configs || [])
  const pipelineStages = useSelector((state: RootState) => state.globalConfigs?.pipeline_stages || [])
  const fundOptions = useSelector((state: any) => state.globalConfigs?.funds || [])

  // Get the default Fundraising pipeline ID
  const defaultFundraisingPipelineId = useMemo((): string | null => {
    const defaultPipeline = pipelines.find(
      (pipeline: { default?: boolean; type?: string; id?: string }) =>
        pipeline.default === true && pipeline.type === PIPELINE_TYPE_FUNDRAISING
    ) as { id?: string } | undefined

    return defaultPipeline?.id || null
  }, [pipelines])

  // Currency functionality for number formatting
  const { getCurrencyById } = useCurrencies()

  // Normalize fund options to ensure they have label property
  // Fund picker gate (fund status, 2026-09-04). scope 'fundraising': this
  // chooses a fund to RAISE INTO, so a closed raise does not belong in the
  // list.
  //
  // NOTE `fundOptions` above is the RAW funds array despite its name, and it
  // stays ungated — the pipeline-origination resolver reads through it, and a
  // gated-out fund must still resolve its pipeline.
  const { options: normalizedFundOptions, optionsFooter: fundOptionsFooter } = useFundFieldOptions({
    scope: 'fundraising'
  })

  const applyStageMoveRights = useStageMoveRights()

  // Function to get pipeline stage options for a specific prospect
  const getPipelineStageOptionsForProspect = useCallback(
    (prospect: ProspectRecord) => {
      const prospectPipelineId = getEffectiveFundraisingPipelineIdForProspect(
        prospect,
        fundOptions,
        defaultFundraisingPipelineId
      )

      if (!prospectPipelineId || !pipelineConfigs.length || !pipelineStages.length) {
        return []
      }

      const filteredConfigs = pipelineConfigs.filter(
        (config) =>
          String(config.pipeline) === String(prospectPipelineId) && config.visible_tbl === true
      )

      const allPipelineStageOptions = filteredConfigs
        .map((config) => {
          const stage = pipelineStages.find((s) => s.id === config.stage)
          return {
            value: config.id,
            label: stage?.name || '',
            sequence: config.sequence_tbl || config.sequence_kb || 0
          }
        })
        .filter((option) => option.label && typeof option.label === 'string')
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''))

      // Stages the user may not move this prospect into or out of are disabled
      // with the reason (pipeline_stage.move).
      return applyStageMoveRights(allPipelineStageOptions, prospect[ProspectRecordFields.pipeline_prospects__stage])
    },
    [defaultFundraisingPipelineId, fundOptions, pipelineConfigs, pipelineStages, applyStageMoveRights]
  )

  // Handle successful PATCH requests
  const handlePatchSuccess = useCallback(
    async (recordId: string, updates: Record<string, any>) => {
      // Check if this is a contact relationship field update
      // When using valueTransformer, updates will contain userFields and relationship_category_id
      // When not using valueTransformer, updates will contain the contact field names directly
      const isContactRelationshipUpdate =
        updates[ProspectFields.contact_prospects__lead] !== undefined ||
        updates[ProspectFields.contact_accounts__introduced_by] !== undefined ||
        updates[ProspectFields.contact_accounts__sourced_by] !== undefined ||
        (updates.userFields !== undefined && updates.relationship_category_id !== undefined)

      // If contact relationships were updated, fetch full prospect to get updated contact arrays
      const currentToken = getAccessToken(store.getState().auth)
      if (isContactRelationshipUpdate && recordId && currentToken) {
        try {
          const response = await fetchFundraisingById(recordId, currentToken)
          if (response.data && !response.error) {
            // Update Redux state with fetched prospect data (includes updated contact arrays)
            dispatch(
              updateProspectRecord({
                prospectId: recordId,
                updates: response.data
              })
            )
            return
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error fetching prospect after contact update:', error)
          }
          // Fall through to regular update if fetch fails
        }
      }

      // Fund change: align pipeline origination + stage with the new fund (same rules as drawer/full form).
      if (updates[ProspectFields.prospects__fund] !== undefined) {
        const row = findProspectRowInTable(fundraisingData as ProspectRecord[], recordId)
        const fundId = normalizeFundIdFromPatch(updates[ProspectFields.prospects__fund])
        const merged: Record<string, any> = row
          ? {
              ...row,
              ...updates,
              [ProspectFields.prospects__fund]: fundId ?? updates[ProspectFields.prospects__fund]
            }
          : { ...updates }

        const { pipelineOrigination, stageReset, pipelineProspectsRowId, needsPipelineProspectsStagePatch } =
          applyFundChangeToUpdates(updates, merged, {
            fundOptions,
            pipelineConfigs,
            pipelineStages,
            defaultFundraisingPipelineId
          })

        await syncFundChangePipelineOnServer(
          recordId,
          pipelineOrigination,
          needsPipelineProspectsStagePatch,
          stageReset,
          pipelineProspectsRowId,
          () => getAccessToken(store.getState().auth)
        )
      }

      // Update pipeline stage label when pipeline stage is updated
      if (updates[ProspectFields.pipeline_prospects__stage]) {
        const pipelineConfigId = updates[ProspectFields.pipeline_prospects__stage]
        // Find the pipeline config
        const config = pipelineConfigs.find((c: any) => c.id === pipelineConfigId)
        if (config) {
          // Find the pipeline stage
          const stage = pipelineStages.find((s: any) => s.id === config.stage)
          if (stage && stage.name) {
            updates['pipeline_prospects__stage__label'] = stage.name
          }
        }
      }

      // Denormalized fund label for table cells that read `funds__name`; normalize id if select sent {value,label}
      if (updates[ProspectFields.prospects__fund] !== undefined) {
        const fid = normalizeFundIdFromPatch(updates[ProspectFields.prospects__fund])
        if (fid != null) {
          updates[ProspectFields.prospects__fund] = fid
          const fundRow = fundOptions.find((f: { id?: string; value?: string }) => (f.id ?? f.value) === fid)
          const name = fundRow?.name ?? fundRow?.label
          if (name != null && name !== '') {
            updates['funds__name'] = name
          }
        }
      }

      // Update prospect record in Redux
      // Note: For account fields (like Country, City), recordId is the prospect ID (set by recordIdForPatch)
      // Account field updates are stored in the prospect record, so we update the prospect record
      if (recordId) {
        dispatch(
          updateProspectRecord({
            prospectId: recordId,
            updates
          })
        )
      }
    },
    [
      dispatch,
      pipelineConfigs,
      pipelineStages,
      fundraisingData,
      fundOptions,
      defaultFundraisingPipelineId
    ]
  )

  // InputManager render function for cell editing
  const renderInputManagerColumn = useCallback(
    (
      record: ProspectRecord,
      columnKey: string,
      fieldId: string,
      fieldType: FieldTypeEnum,
      editingContext: any,
      columnInfo: any = {},
      action?: any
    ) => {
      const isRequired = columnInfo.required || false
      const columnTitle = columnInfo.title || columnKey
      const isColumnEditable = columnInfo.editable !== false

      // Get updated record from Redux state instead of using static record
      // Match by id or prospects__id to handle different ID formats
      const recordId = record.id || record[ProspectFields.prospects__id] || (record as any).prospects__id
      const updatedRecord =
        fundraisingData?.find(
          (prospect: ProspectRecord) =>
            prospect.id === recordId ||
            prospect[ProspectFields.prospects__id] === recordId ||
            (prospect as any).prospects__id === recordId
        ) || record

      const reportingModuleKey = columnInfo.moduleKey as string | undefined
      const inputManagerModule: TableInputManagerModule = reportingModuleKeyToTableInputManagerModule(
        reportingModuleKey,
        'fundraising'
      )

      const fundraisingHelpers = {
        getCurrencyById,
        getLookupOptions,
        getPipelineStageOptionsForProspect,
        normalizedFundOptions,
        fundOptionsFooter,
        originalRecord: record,
        recordId: record.id || recordId
      }
      const companiesHelpers = {
        getCurrencyById,
        getLookupOptions,
        getRoleOptions,
        getFundOptions
      }
      const contactsHelpers = {
        getLookupLabel,
        getLookupOptions
      }

      const columnDef = {
        ...columnInfo,
        key: columnKey,
        dataIndex: columnKey,
        title: columnTitle,
        required: isRequired,
        editable: isColumnEditable
      }

      let fieldConfig: ReturnType<typeof mapColumnToInputManagerField>
      if (inputManagerModule === 'companies') {
        fieldConfig = mapColumnToInputManagerField(
          'companies',
          columnDef,
          updatedRecord,
          companiesHelpers,
          fieldType
        )
      } else if (inputManagerModule === 'contacts') {
        fieldConfig = mapColumnToInputManagerField(
          'contacts',
          columnDef,
          updatedRecord,
          contactsHelpers,
          fieldType
        )
      } else {
        fieldConfig = mapColumnToInputManagerField(
          'fundraising',
          columnDef,
          updatedRecord,
          fundraisingHelpers,
          fieldType
        )
      }

      // Ensure field key is unique per row in Fundraising tables.
      // Without this, some shared mappers can return the same key for each row (e.g. account city),
      // causing InputManager's global edit state to toggle all matching cells at once.
      const rowScopedFieldId = String(
        updatedRecord.id ||
          updatedRecord[ProspectFields.prospects__id] ||
          (updatedRecord as any).prospects__id ||
          record.id ||
          record[ProspectFields.prospects__id] ||
          (record as any).prospects__id ||
          'unknown-row'
      )
      fieldConfig = {
        ...fieldConfig,
        key: `${rowScopedFieldId}-${String(fieldConfig?.key ?? columnKey)}`
      }

      // Handle field value changes
      const handleFieldChange = (fieldKey: string, newValue: any) => {
        // Update the cell data
        if (editingContext.onCellDataChange) {
          editingContext.onCellDataChange(newValue)
        }
      }

      // Handle field edit state changes for column width expansion
      const handleFieldEdit = (isEditing: boolean) => {
        if (editingContext?.onFieldEdit) {
          editingContext.onFieldEdit(columnKey, isEditing)
        }
      }

      // Handle field error
      const handleFieldError = (fieldKey: string, error: any) => {
        if (process.env.NODE_ENV === 'development') {
          console.error(`Error in field ${fieldKey}:`, error)
        }
      }

      // Alignment
      const getFieldAlignment = () => {
        if (
          fieldType === FieldTypeEnum.CURRENCY ||
          fieldType === FieldTypeEnum.NUMBER ||
          fieldType === FieldTypeEnum.VALUATION
        ) {
          return 'right'
        }
        return 'left'
      }

      // Apply truncation to all columns except tag displays
      const needsTruncation = !columnInfo.tagDisplay

      // Determine patchId - for prospect fields, ensure we use prospects__id
      // For account fields (like Country, City), use account ID for patching but prospect ID for Redux
      // Record ids are uuid strings; a numeric id (legacy rows) is stringified the way the PATCH URL would anyway.
      const toId = (v: unknown): string | undefined =>
        typeof v === 'string' ? v : typeof v === 'number' ? String(v) : undefined
      let patchId: string | undefined = toId(record[fieldId] || record.id)
      let recordIdForPatch = record.id || record[ProspectFields.prospects__id] || (record as any).prospects__id

      // For prospect fields (fields starting with 'prospects__'), always use the correct prospect ID
      if (columnKey && columnKey.startsWith('prospects__')) {
        patchId = toId(
          updatedRecord[ProspectFields.prospects__id] ||
            updatedRecord.prospects__id ||
            record[ProspectFields.prospects__id] ||
            record.prospects__id ||
            patchId
        )
        recordIdForPatch = patchId
      } else if (columnKey && columnKey.startsWith('accounts__')) {
        // For account fields, use account ID for patching but prospect ID for Redux updates
        // Account ID comes from fieldId (AccountRecordFields.accounts__id) or from the record
        patchId = toId(
          record[fieldId] ||
            updatedRecord[AccountRecordFields.accounts__id] ||
            updatedRecord[ProspectFields.prospects__investor] ||
            record[AccountRecordFields.accounts__id] ||
            record[ProspectFields.prospects__investor] ||
            patchId
        )
        // Keep recordIdForPatch as prospect ID for Redux updates
        recordIdForPatch =
          updatedRecord[ProspectFields.prospects__id] ||
          updatedRecord.id ||
          record[ProspectFields.prospects__id] ||
          record.id ||
          recordIdForPatch
      } else if (columnKey === 'funds__name') {
        // Fund column uses moduleKey=funds so fieldId is funds__id; PATCH /prospects must use the prospect id, not the fund id.
        // Use same id preference as fundraising reducer getProspectId (id first) so onPatchSuccess matches UPDATE_PROSPECT_RECORD.
        const canonicalProspectId =
          updatedRecord.id ||
          updatedRecord[ProspectFields.prospects__id] ||
          updatedRecord.prospects__id ||
          record.id ||
          record[ProspectFields.prospects__id] ||
          record.prospects__id
        patchId = canonicalProspectId
        recordIdForPatch = canonicalProspectId
      }

      // Fallback to record.id if still not found
      if (!patchId) {
        patchId = toId(record.id)
      }
      if (!recordIdForPatch) {
        recordIdForPatch = record.id
      }

      // Always return InputManager - it handles edit/display mode internally
      return (
        <div
          style={{
            width: '100%',
            ...(needsTruncation
              ? {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }
              : {})
          }}
        >
          <InputManager
            inputMode='read'
            labelPosition='none'
            alignment={getFieldAlignment()}
            fields={[fieldConfig]}
            onChange={handleFieldChange}
            onSave={() => {}} // Auto-Patch managed
            onError={handleFieldError}
            onPatchSuccess={(rid, updates) => {
              if (inputManagerModule === 'companies') {
                const accountId =
                  record[AccountRecordFields.accounts__id] || record[ProspectFields.prospects__investor]
                if (accountId) {
                  dispatch(updateCompanyRecord({ companyId: String(accountId), updates: { ...updates } }))
                }
              }
              if (inputManagerModule === 'contacts') {
                const contactId = (record as Record<string, string | undefined>).contacts__id
                if (contactId) {
                  dispatch(updateContactRecord({ contactId: String(contactId), updates: { ...updates } }))
                }
              }
              handlePatchSuccess(String(rid), updates)
            }}
            onFieldEdit={handleFieldEdit}
            enableAutoPatch={true}
            patchId={patchId}
            recordId={recordIdForPatch || ''}
            className='cell-edit-input-manager'
            onClick={action}
            textClassName={needsTruncation ? 'truncate' : undefined}
            currentRecord={updatedRecord}
          />
        </div>
      )
    },
    [
      dispatch,
      getCurrencyById,
      getLookupOptions,
      getLookupLabel,
      getRoleOptions,
      getFundOptions,
      handlePatchSuccess,
      fundraisingData,
      getPipelineStageOptionsForProspect,
      normalizedFundOptions,
      fundOptionsFooter
    ]
  )

  // Refs must be updated during render (not in useEffect). Column render runs in the same commit as Table;
  // useEffect runs after paint, so cells would read stale fundraisingData until another rerender (e.g. tab switch).
  const handlePrimaryActionRef = useRef(onProspectNameClick)
  const handleSecondaryActionRef = useRef(() => {})
  const handleTasksClickRef = useRef(onTasksClick)
  const handleTaskToggleRef = useRef(onTaskToggle)
  const renderInputManagerColumnRef = useRef(renderInputManagerColumn)

  handlePrimaryActionRef.current = onProspectNameClick
  handleTasksClickRef.current = onTasksClick
  handleTaskToggleRef.current = onTaskToggle
  renderInputManagerColumnRef.current = renderInputManagerColumn

  const { columns, frozenColumns } = useMemo(() => {
    return createTableColumns({
      columns: gridColumns,
      handlePrimaryActionRef,
      handleSecondaryActionRef,
      handleTasksClickRef,
      handleTaskToggleRef,
      renderInputManagerColumnRef,
      viewTaskConfig: VIEW_TASK_CONFIG.fundraising,
      fallbackRecordModuleKey: 'prospects'
    })
  }, [gridColumns])

  // Deduplicate fundraising by id and flatten nested fields for table display
  const dataSource = useMemo(() => {
    if (!fundraisingData || fundraisingData.length === 0) return []

    const seenIds = new Set()
    return fundraisingData
      .map((prospect: ProspectRecord) => {
        // Normalize: ensure id field exists from prospects__id if missing
        const prospectsId = (prospect as any)['prospects__id']
        const normalizedId = prospect.id || prospectsId

        if (!normalizedId) {
          return null // Skip records without any ID
        }

        return {
          ...prospect,
          id: normalizedId,
          key: normalizedId
        }
      })
      .filter((prospect): prospect is ProspectRecord & { id: string; key: string } => {
        if (!prospect) return false
        const prospectId = prospect.id
        if (!prospectId || seenIds.has(prospectId)) {
          return false
        }
        seenIds.add(prospectId)
        return true
      })
  }, [fundraisingData])

  return (
    <div className='flex-1 min-h-0 h-full w-full'>
      <TableView
        columns={columns}
        dataSource={dataSource}
        onLoadMore={onLoadMore}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        currentPage={currentPage}
        enableCellEditing={true}
        currentFilters={(() => {
          return Array.isArray(activeFilters)
            ? activeFilters.reduce((acc: any, filter: any) => {
                acc[filter.key] = filter.value
                return acc
              }, {})
            : activeFilters || {}
        })()}
        onFiltersChange={onFiltersChange}
        onSortChange={onSortChange}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        currentSortConfig={
          Array.isArray(activeSorting) && activeSorting.length > 0
            ? { column: activeSorting[0].key, direction: activeSorting[0].direction }
            : null
        }
        frozenColumns={frozenColumns}
      />
    </div>
  )
}

export default FundraisingTable
